import { useState, useCallback, useMemo } from 'react';
import { collection, doc, setDoc, addDoc, getDoc, deleteDoc, getDocs, query, runTransaction, where, limit, orderBy } from 'firebase/firestore';
import { appFirestore } from '../../index';
import { roadBookFBConverter } from '../views/logistics/RoadBookFBConverter';
import { RRRoadBookType } from '../views/logistics/RRRoadBookType';

export default function useRoadBookCrud() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const saveRoadBook = useCallback(async (roadBook: RRRoadBookType) => {
        setLoading(true);
        setError(null);
        try {
            const coll = collection(appFirestore, 'roadBooks').withConverter(roadBookFBConverter as any);
            if (roadBook.id) {
                const d = doc(coll, roadBook.id);
                await setDoc(d, roadBook, {merge: true});
                setLoading(false);
                return roadBook.id;
            } else {
                const res = await addDoc(coll, roadBook);
                setLoading(false);
                return res.id;
            }
        } catch (e: any) {
            setError(e);
            setLoading(false);
            throw e;
        }
    }, []);

    const getRoadBookById = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            const d = doc(appFirestore, 'roadBooks', id).withConverter(roadBookFBConverter as any);
            const snap = await getDoc(d);
            setLoading(false);
            return snap.exists() ? snap.data() as RRRoadBookType : null;
        } catch (e: any) {
            setError(e);
            setLoading(false);
            throw e;
        }
    }, []);

    const deleteRoadBook = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);
        try {
            const d = doc(appFirestore, 'roadBooks', id);
            await deleteDoc(d);
            setLoading(false);
        } catch (e: any) {
            setError(e);
            setLoading(false);
            throw e;
        }
    }, []);

    const findByFormNumber = useCallback(async (formNumber: number | string, employeeId?: string, date?: Date, vehiclePlate?: string) => {
         try {
             const coll = collection(appFirestore, 'roadBooks').withConverter(roadBookFBConverter as any);

             const fnNumeric = (formNumber === undefined || formNumber === null || formNumber === '') ? undefined : Number(formNumber);

             // If we have employeeId and date, constrain to the day and optionally vehiclePlate
             if (employeeId && date) {
                 const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                 const end = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
                 let q;
                 if (vehiclePlate) {
                     q = query(coll,
                         where('employeeId', '==', employeeId),
                         where('date', '>=', start),
                         where('date', '<', end),
                         where('vehiclePlate', '==', vehiclePlate),
                         where('formNumber', '==', fnNumeric),
                         limit(1)
                     );
                 } else {
                     q = query(coll,
                         where('employeeId', '==', employeeId),
                         where('date', '>=', start),
                         where('date', '<', end),
                         where('formNumber', '==', fnNumeric),
                         limit(1)
                     );
                 }
                 const snaps = await getDocs(q);
                 if (snaps.empty) return null;
                 const d = snaps.docs[0];
                 return { id: d.id, ...(d.data() as any) };
             }

             // If only vehiclePlate provided, search within that vehicle
             if (vehiclePlate) {
                 const q = query(coll, where('formNumber', '==', fnNumeric), where('vehiclePlate', '==', vehiclePlate), limit(1));
                 const snaps = await getDocs(q);
                 if (snaps.empty) return null;
                 const d = snaps.docs[0];
                 return { id: d.id, ...(d.data() as any) };
             }

             // fallback: scan all (not recommended for large collections)
             const snaps = await getDocs(query(coll));
             const found = snaps.docs.map(d => ({ id: d.id, ...(d.data() as any) })).find((r: any) => {
                 return (r.formNumber !== undefined && r.formNumber !== null) && fnNumeric === Number(r.formNumber);
             });
             return found || null;
         } catch (e: any) {
             setError(e);
             throw e;
         }
    }, []);

    const getNextFormNumber = useCallback(async (_vehiclePlate?: string) => {
         try {
             // Use efficient query: get the document with the highest formNumber
             const coll = collection(appFirestore, 'roadBooks').withConverter(roadBookFBConverter as any);
             const snaps = await getDocs(query(coll, orderBy('formNumber', 'desc'), limit(1)));
             let max = 0;
             if (!snaps.empty) {
                 const d = snaps.docs[0];
                 const data: any = d.data();
                 const fn = data.formNumber;
                 const n = Number(fn);
                 if (!Number.isNaN(n)) max = n;
             }
             return max + 1;
         } catch (e: any) {
             setError(e);
             throw e;
         }
    }, []);

    const sanitizeId = (s?: string) => {
        if (!s) return 'global';
        return s.toString().replace(/[^a-zA-Z0-9_-]/g, '_');
    };

    const getNextFormNumberAtomic = useCallback(async (vehiclePlate?: string) => {
         try {
             const id = `roadBooks_${sanitizeId(vehiclePlate)}`; // ensure unique counter per vehicle
             const counterRef = doc(appFirestore, 'counters', id);
             const newNum = await runTransaction(appFirestore, async (transaction) => {
                 const snap = await transaction.get(counterRef);
                 let last = 0;
                 if (snap.exists()) {
                     const data: any = snap.data();
                     last = data.lastNumber || 0;
                 }
                 const next = last + 1;
                 transaction.set(counterRef, { lastNumber: next }, { merge: true });
                 return next;
             });
             return newNum;
         } catch (e: any) {
             setError(e);
             throw e;
         }
    }, []);

    const reserveRoadBooksAtomically = useCallback(async (employeeId: string, date: Date, payloads: Array<{vehiclePlate: string, payload: any}>) => {
         // NOTE: Per user request we do NOT use a separate counters collection.
         // Approach: compute global max formNumber by scanning `roadBooks`, then reserve a contiguous block of numbers
         // by creating documents inside a transaction. Because we can't query inside a transaction in the web SDK,
         // we perform an optimistic reservation with verification+retry. This reduces collisions and uses a transaction
         // for creation to ensure all new docs are written together.
         const collRef = collection(appFirestore, 'roadBooks');

         const maxRetries = 5;
         let attempt = 0;

         // Helper to chunk arrays into size <= 10 for Firestore 'in' queries
         function chunkArray<T>(arr: T[], chunkSize: number): T[][] {
             const res: T[][] = [];
             for (let i = 0; i < arr.length; i += chunkSize) res.push(arr.slice(i, i + chunkSize));
             return res;
         }

         // small helper to sleep (defined outside loop to avoid creating a function inside a loop)
         const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

         while (attempt < maxRetries) {
             attempt++;
             try {
                 // 1) efficiently find the current global max formNumber using an ordered query
                 const top = await getDocs(query(collRef, orderBy('formNumber', 'desc'), limit(1)));
                 let max = 0;
                 if (!top.empty) {
                     const d = top.docs[0];
                     const data: any = d.data();
                     const fn = data.formNumber;
                     const n = Number(fn);
                     if (!Number.isNaN(n)) max = n;
                 }

                 // 2) prepare doc refs and assigned numbers
                 const docRefs = payloads.map(() => doc(collRef));
                 const assignedNumbers = payloads.map((_, idx) => (max + idx + 1));

                 // 3) run transaction to create all new docs (all or none)
                 await runTransaction(appFirestore, async (transaction) => {
                     for (let i = 0; i < payloads.length; i++) {
                         const item = payloads[i];
                         const nr = assignedNumbers[i];
                         const ref = docRefs[i];
                         const toSave = {
                             employeeId: employeeId,
                             employeeName: item.payload.employeeName || '',
                             date: date,
                             formNumber: nr,
                             vehiclePlate: item.vehiclePlate,
                             startKm: item.payload.startKm ?? null,
                             endKm: item.payload.endKm ?? null,
                             rows: item.payload.rows || [],
                             createdAt: new Date(),
                             updatedAt: new Date()
                         };
                         transaction.set(ref, toSave);
                     }
                 });

                 // 4) verify uniqueness: query for any duplicates of the assignedNumbers
                 // Firestore 'in' supports up to 10 items, so chunk if needed
                 let duplicateFound = false;
                 const verificationChunks = chunkArray(assignedNumbers.map(String), 10);
                 for (const chunk of verificationChunks) {
                     const q = query(collRef, where('formNumber', 'in', chunk as any));
                     const vs = await getDocs(q);
                     // build counts per formNumber
                     const counts: Record<string, number> = {};
                     vs.docs.forEach(d => {
                         const fn = (d.data() as any).formNumber;
                         if (!fn) return;
                         counts[String(fn)] = (counts[String(fn)] || 0) + 1;
                     });
                     // if any assigned number appears more than once, collision
                     for (const n of chunk) {
                         if ((counts[n] || 0) > 1) { duplicateFound = true; break; }
                     }
                     if (duplicateFound) break;
                 }

                 if (!duplicateFound) {
                     // success — return mapping of vehiclePlate -> id/formNumber
                     return payloads.map((p, idx) => ({ vehiclePlate: p.vehiclePlate, id: docRefs[idx].id, formNumber: assignedNumbers[idx] }));
                 }

                 // collision: remove the docs we created, then retry
                 for (const ref of docRefs) {
                     try { await deleteDoc(ref); } catch {/* best-effort */}
                 }
                 // loop to retry
             } catch (e: any) {
                 setError(e);
                 // If error occurs, either retry or throw depending on attempt
                 if (attempt >= maxRetries) throw e;
                 // small backoff
                 await sleep(200 * attempt);
             }
         }

         throw new Error('Неуспешна резервация на номера за roadBooks след няколко опита');
    }, []);

    const findByEmployeeDate = useCallback(async (employeeId: string, date: Date, vehiclePlate?: string) => {
         try {
             const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
             const end = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
             const coll = collection(appFirestore, 'roadBooks').withConverter(roadBookFBConverter as any);
             let q;
             if (vehiclePlate) {
                 q = query(coll, where('employeeId', '==', employeeId), where('date', '>=', start), where('date', '<', end), where('vehiclePlate', '==', vehiclePlate), limit(1));
             } else {
                 q = query(coll, where('employeeId', '==', employeeId), where('date', '>=', start), where('date', '<', end), limit(1));
             }
             const snaps = await getDocs(q);
             if (snaps.empty) return null;
             const d = snaps.docs[0];
             return { id: d.id, ...(d.data() as any) };
         } catch (e: any) {
             setError(e);
             throw e;
         }
    }, []);

    return useMemo(() => ({
        loading,
        error,
        saveRoadBook,
        getRoadBookById,
        deleteRoadBook,
        findByFormNumber,
        getNextFormNumber,
        getNextFormNumberAtomic,
        reserveRoadBooksAtomically,
        findByEmployeeDate
    }), [loading, error, saveRoadBook, getRoadBookById, deleteRoadBook, findByFormNumber, getNextFormNumber, getNextFormNumberAtomic, reserveRoadBooksAtomically, findByEmployeeDate]);
 }
